import React, { useState } from 'react';
import { Input, Button, Modal, Spin, Select, InputNumber, Table, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSalesReturnForm } from './hooks/useSalesReturnForm';
import { pw2204f1Api } from '../../services/pw2204f1Api';
import { BatchAcceptanceBridge, pw2204f1AcceptanceManifest } from '../../features/acceptance';

const PW2204F1Form: React.FC = () => {
  const {
    mode, isEditing, loading, records, current, currentIdx,
    warehouse, setWarehouse, docDate, setDocDate,
    personCode, setPersonCode, customerCode, setCustomerCode,
    invoiceNo, setInvoiceNo, description, setDescription,
    taxType, setTaxType, amountReceived, setAmountReceived,
    transportFee, setTransportFee, shippingFee, setShippingFee,
    remark, setRemark, jumpType, setJumpType,
    details, addDetailRow, removeDetailRow, updateDetail,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete, quickSearch,
  } = useSalesReturnForm();

  const [searchText, setSearchText] = useState('');
  const isApproved = current?.f2140_80 === 'P';
  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';
  const detailSubtotal = details.reduce(
    (sum, row) => sum + Math.round((row.f2141_13 || 0) * (row.f2141_14 || 0) * 100) / 100,
    0,
  );
  const displayedSubtotal = mode === 'browse' ? current?.f2140_17 || 0 : detailSubtotal;
  const displayedUntaxed = mode === 'browse'
    ? current?.f2140_28 || 0
    : displayedSubtotal + transportFee + shippingFee;
  const displayedTax = mode === 'browse'
    ? current?.f2140_19 || 0
    : taxType === 1 ? Math.round(displayedUntaxed * 0.05) : 0;
  const displayedTotal = mode === 'browse'
    ? current?.f2140_20 || 0
    : displayedUntaxed + displayedTax;

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除', content: `確定要刪除退回單「${current.f2140_01}」？`,
      okText: '刪除', okType: 'danger', cancelText: '取消', onOk: doDelete,
    });
  };

  const detailColumns = [
    {
      title: '#',
      width: 40,
      onHeaderCell: () => ({ 'data-contract-field': 'f2141_02' } as React.ThHTMLAttributes<HTMLTableCellElement>),
      render: (_: any, __: any, i: number) => i + 1,
    },
    { title: '產品代號', dataIndex: 'f2141_11', width: 120, render: (v: string, _: any, i: number) => (
      <Input value={v} onChange={e => updateDetail(i, 'f2141_11', e.target.value)} disabled={!isEditing} maxLength={12} />
    )},
    { title: '產品名稱', dataIndex: 'f2141_11a', width: 180, render: (v: string, _: any, i: number) => (
      <Input value={v} onChange={e => updateDetail(i, 'f2141_11a', e.target.value)} disabled={!isEditing} maxLength={60} />
    )},
    { title: '單位', dataIndex: 'f2141_12', width: 60, render: (v: string, _: any, i: number) => (
      <Input value={v} onChange={e => updateDetail(i, 'f2141_12', e.target.value)} disabled={!isEditing} maxLength={4} />
    )},
    { title: '數量', dataIndex: 'f2141_13', width: 100, render: (v: number, _: any, i: number) => (
      <InputNumber value={v} onChange={val => updateDetail(i, 'f2141_13', val || 0)} disabled={!isEditing} style={{ width: '100%' }} />
    )},
    { title: '單價', dataIndex: 'f2141_14', width: 100, render: (v: number, _: any, i: number) => (
      <InputNumber value={v} onChange={val => updateDetail(i, 'f2141_14', val || 0)} disabled={!isEditing} style={{ width: '100%' }} />
    )},
    { title: '金額', width: 100, render: (_: any, row: any) => (
      <span>{((row.f2141_13 || 0) * (row.f2141_14 || 0)).toFixed(2)}</span>
    )},
    ...(isEditing ? [{
      title: '#',
      width: 40,
      onHeaderCell: () => ({ 'data-acceptance-non-field': 'row_delete_action' } as React.ThHTMLAttributes<HTMLTableCellElement>),
      render: (_: any, __: any, i: number) => (
      <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeDetailRow(i)} disabled={details.length <= 1} />
      ),
    }] : []),
  ];

  return (
    <Spin spinning={loading}>
      <div className="max-w-5xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          銷貨退回單 (pw2204f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
          {isApproved && <Tag color="green" className="ml-2">已核准</Tag>}
        </h2>

        <div className="flex items-center gap-2 mb-4">
          <Input placeholder="輸入單號/客戶查詢" value={searchText} onChange={e => setSearchText(e.target.value)}
            onPressEnter={() => quickSearch(searchText.trim())} disabled={isEditing} style={{ width: 300 }} />
          <Button onClick={() => quickSearch(searchText.trim())} disabled={isEditing}>查詢</Button>
        </div>

        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Header fields */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">退回單號</label>
            <Input value={current?.f2140_01 || ''} disabled style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">日期 <span className="text-red-500">*</span></label>
            <Input type="date" value={docDate} onChange={e => setDocDate(e.target.value)} disabled={!isEditing} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">庫號</label>
            <Input value={warehouse} onChange={e => setWarehouse(e.target.value)} disabled={!isEditing} maxLength={4} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">客戶代號</label>
            <Input value={customerCode} onChange={e => setCustomerCode(e.target.value)} disabled={!isEditing} maxLength={12} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">人員代號</label>
            <Input value={personCode} onChange={e => setPersonCode(e.target.value)} disabled={!isEditing} maxLength={8} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">發票號碼</label>
            <Input value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)} disabled={!isEditing} maxLength={12} />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">單據說明</label>
            <Input value={description} onChange={e => setDescription(e.target.value)} disabled={!isEditing} maxLength={80} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">含稅別 <span className="text-red-500">*</span></label>
            <Select data-contract-field="f2140_18" value={taxType} onChange={setTaxType} disabled={!isEditing} style={{ width: '100%' }}>
              <Select.Option value={1}>含稅</Select.Option>
              <Select.Option value={2}>未稅</Select.Option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" data-contract-field="f2140_31">跳號別</label>
            <Select
              value={jumpType}
              onChange={setJumpType}
              disabled={mode !== 'add'}
              style={{ width: '100%' }}
              data-contract-field="f2140_31"
            >
              <Select.Option value="">標準</Select.Option>
              <Select.Option value="M">M類</Select.Option>
              <Select.Option value="P">P類</Select.Option>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">交通費</label>
            <InputNumber value={transportFee} onChange={v => setTransportFee(v || 0)} disabled={!isEditing} style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">運費</label>
            <InputNumber value={shippingFee} onChange={v => setShippingFee(v || 0)} disabled={!isEditing} style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">已收金額</label>
            <InputNumber value={amountReceived} onChange={v => setAmountReceived(v || 0)} disabled={!isEditing} style={{ width: '100%' }} />
          </div>
        </div>

        {/* Amounts summary */}
        <div className="grid grid-cols-4 gap-4 mb-4 p-3 bg-gray-50 rounded">
          <div><label className="text-gray-500" data-contract-field="f2140_17">小計:</label> {displayedSubtotal.toFixed(2)}</div>
          <div><label className="text-gray-500" data-contract-field="f2140_28">未稅:</label> {displayedUntaxed.toFixed(2)}</div>
          <div><label className="text-gray-500" data-contract-field="f2140_19">稅額:</label> {displayedTax.toFixed(0)}</div>
          <div><label className="text-gray-500" data-contract-field="f2140_20">含稅總額:</label> {displayedTotal.toFixed(2)}</div>
        </div>

        {/* Details table */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium">退回明細</span>
            {isEditing && <Button size="small" icon={<PlusOutlined />} onClick={addDetailRow}>新增行</Button>}
          </div>
          <Table dataSource={details} columns={detailColumns} rowKey={(_, i) => String(i)}
            pagination={false} size="small" scroll={{ x: 800 }} />
        </div>

        {/* Remark */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">備註</label>
          <Input.TextArea value={remark} onChange={e => setRemark(e.target.value)} disabled={!isEditing} rows={2} />
        </div>

        {/* Audit */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-4">
            建立人員: {current.f2140_91} | 日期: {current.f2140_92 || '-'} | 修改: {current.f2140_93}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current || isApproved}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current || isApproved}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>
        <BatchAcceptanceBridge
          manifest={pw2204f1AcceptanceManifest}
          records={records}
          selectedKeys={current?.f2140_01 ? [current.f2140_01] : []}
          keyOf={(item) => item.f2140_01}
          queryExact={(key) => pw2204f1Api.get(key)}
          lastApiResponse={current}
          loading={loading}
          onAutoFill={navFirst}
          autoFillDisabled={records.length === 0}
          screenDraft={{ warehouse, docDate, personCode, customerCode, invoiceNo, description, taxType, amountReceived, transportFee, shippingFee, remark, jumpType, details }}
        />
      </div>
    </Spin>
  );
};

export default PW2204F1Form;
